import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { pluggyCredenciaisApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'

/**
 * Aviso curto mostrado ANTES de abrir a conexão com o banco (botão "Conectar banco" da página Pluggy e do guia
 * "Primeiros passos"). Se a pessoa ainda não cadastrou as próprias chaves da Pluggy, explica como criar a conta
 * no Meu Pluggy e leva a Configurações em vez de abrir a conexão.
 */
export function AvisoConexao({ open, onContinuar, onCancelar }: { open: boolean; onContinuar: () => void; onCancelar: () => void }) {
  const navigate = useNavigate()
  const { data: credencial, isLoading } = useQuery({
    queryKey: ['pluggy-credenciais'],
    queryFn: pluggyCredenciaisApi.get,
    retry: false,
    enabled: open,
  })
  const temChaves = !!credencial?.possuiApiKey

  return (
    <Modal
      open={open}
      onClose={onCancelar}
      title="Antes de conectar o banco"
      size="md"
      footer={
        isLoading ? undefined : (
          <>
            <Button variant="light" onClick={onCancelar}>Cancelar</Button>
            {temChaves ? (
              <Button icon="ri-arrow-right-line" onClick={onContinuar}>Continuar</Button>
            ) : (
              <Button icon="ri-settings-3-line" onClick={() => { onCancelar(); navigate('/configuracoes') }}>Ir para Configurações</Button>
            )}
          </>
        )
      }
    >
      <div data-tutorial="conta.aviso" className="space-y-3 text-13 text-muted">
        {isLoading ? (
          <LoadingState />
        ) : (
          <>
            <ul className="space-y-2">
              <li className="flex gap-2"><i className="ri-building-line mt-0.5 text-primary" aria-hidden /><span>A conexão é feita pela Pluggy, uma empresa independente do Walleon. Você autoriza o acesso no ambiente dela.</span></li>
              <li className="flex gap-2"><i className="ri-eye-line mt-0.5 text-primary" aria-hidden /><span>O Walleon só lê saldo e lançamentos. Não movimenta dinheiro.</span></li>
              <li className="flex gap-2"><i className="ri-link-unlink mt-0.5 text-primary" aria-hidden /><span>Você pode desconectar quando quiser e pedir a exclusão dos dados.</span></li>
            </ul>
            <p>
              <Link to="/conectar-bancos#legal" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Ler o aviso completo <i className="ri-external-link-line" aria-hidden />
              </Link>
            </p>
            {!temChaves && (
              <div className="rounded border border-warning/40 bg-warning/10 p-3" role="note">
                <p className="font-medium text-white">Você ainda não cadastrou suas chaves da Pluggy</p>
                <p className="mt-1">
                  Cada pessoa usa a própria conta. Crie a sua no Meu Pluggy (pluggy.ai), copie o Client ID e o Client Secret
                  e cole em Configurações, no bloco da Pluggy. Depois volte e conecte o banco.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
